import { useEffect } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: components in nested directory
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { IBankAccountFormDialogProps } from '@/@types/invoice';
import { useBankAccountForm } from '@/hooks/useBankAccountForm';

const CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF', 'ZAR', 'AED', 'CZK', 'PLN'];

export function BankAccountFormDialog({
  open,
  onOpenChange,
  orgId,
  existingAccount,
}: IBankAccountFormDialogProps) {
  const isEditing = !!existingAccount;
  const createAccount = useMutation(api.bankAccounts.mutations.create);
  const updateAccount = useMutation(api.bankAccounts.mutations.update);

  const { form, setField, resetForm, isSubmitting, setIsSubmitting } =
    useBankAccountForm(existingAccount);

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, resetForm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.bankName.trim() || !form.accountHolderName.trim() || !form.accountNumber.trim()) return;

    setIsSubmitting(true);
    try {
      if (isEditing && existingAccount) {
        await updateAccount({
          accountId: existingAccount._id,
          bankName: form.bankName.trim(),
          accountHolderName: form.accountHolderName.trim(),
          accountNumber: form.accountNumber.trim(),
          iban: form.iban.trim() || undefined,
          swiftCode: form.swiftCode.trim() || undefined,
          currency: form.currency,
          isPrimary: form.isPrimary,
        });
        toast.success('Bank account updated');
      } else {
        await createAccount({
          orgId,
          bankName: form.bankName.trim(),
          accountHolderName: form.accountHolderName.trim(),
          accountNumber: form.accountNumber.trim(),
          iban: form.iban.trim() || undefined,
          swiftCode: form.swiftCode.trim() || undefined,
          currency: form.currency,
          isPrimary: form.isPrimary,
        });
        toast.success('Bank account created');
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save bank account');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Bank Account' : 'Add Bank Account'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="bank-name">Bank Name</Label>
              <Input
                id="bank-name"
                value={form.bankName}
                onChange={e => setField('bankName', e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="account-holder">Account Holder</Label>
              <Input
                id="account-holder"
                value={form.accountHolderName}
                onChange={e => setField('accountHolderName', e.target.value)}
                required
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="account-number">Account Number</Label>
            <Input
              id="account-number"
              value={form.accountNumber}
              onChange={e => setField('accountNumber', e.target.value)}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="iban">IBAN</Label>
              <Input
                id="iban"
                value={form.iban}
                onChange={e => setField('iban', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="swift">SWIFT Code</Label>
              <Input
                id="swift"
                value={form.swiftCode}
                onChange={e => setField('swiftCode', e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="currency">Currency</Label>
            <Select value={form.currency} onValueChange={val => setField('currency', val)}>
              <SelectTrigger id="currency">
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map(c => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="is-primary"
              checked={form.isPrimary}
              onCheckedChange={checked => setField('isPrimary', checked === true)}
            />
            <Label htmlFor="is-primary" className="text-sm font-normal">
              Set as primary bank account
            </Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 aria-hidden="true" className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : isEditing ? (
                'Update'
              ) : (
                'Create'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
